import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const getProductSettings = createAsyncThunk(
    'productSettings/getProductSettings',
    async ({ accountId, token }, { rejectWithValue }) => {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/products/settings?accountId=${accountId}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            });
            if (!response.ok) {
                const errorData = await response.json();
                return rejectWithValue(errorData.message || 'Errore durante il recupero delle impostazioni');
            }
            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const createProductSetting = createAsyncThunk(
    'productSettings/createProductSetting',
    async ({ setting, token }, { rejectWithValue }) => {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/products/settings`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({ setting })
            });
            if (!response.ok) {
                const errorData = await response.json();
                return rejectWithValue(errorData.message || 'Errore durante la creazione dell\'impostazione');
            }
            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updateProductSetting = createAsyncThunk(
    'productSettings/updateProductSetting',
    async ({ setting, token }, { rejectWithValue }) => {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/products/settings`;
            const response = await fetch(url, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({ setting })
            });
            if (!response.ok) {
                const errorData = await response.json();
                return rejectWithValue(errorData.message || 'Errore durante l\'aggiornamento dell\'impostazione');
            }
            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const addSettingValue = createAsyncThunk(
    'productSettings/addSettingValue',
    async ({ settingId, value, token }, { rejectWithValue }) => {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/products/settingValue`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({ settingId, value })
            });
            if (!response.ok) {
                const errorData = await response.json();
                return rejectWithValue(errorData.message || 'Errore durante l\'aggiunta del valore');
            }
            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const removeSettingValue = createAsyncThunk(
    'productSettings/removeSettingValue',
    async ({ settingId, valueId, token }, { rejectWithValue }) => {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/products/settingValue?settingId=${settingId}&valueId=${valueId}`;
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            });
            if (!response.ok) {
                const errorData = await response.json();
                return rejectWithValue(errorData.message || 'Errore durante la rimozione del valore');
            }
            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const productSettingsSlice = createSlice({
    name: 'productSettings',
    initialState: {
        settings: [],
        loading: false,
        error: null,
        createLoading: false,
        updateLoading: false
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // getProductSettings
            .addCase(getProductSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getProductSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.settings = action.payload || [];
            })
            .addCase(getProductSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // createProductSetting
            .addCase(createProductSetting.pending, (state) => {
                state.createLoading = true;
            })
            .addCase(createProductSetting.fulfilled, (state, action) => {
                state.createLoading = false;
                if (action.payload) {
                    state.settings.push(action.payload);
                }
            })
            .addCase(createProductSetting.rejected, (state, action) => {
                state.createLoading = false;
                state.error = action.payload;
            })
            // updateProductSetting
            .addCase(updateProductSetting.pending, (state) => {
                state.updateLoading = true;
            })
            .addCase(updateProductSetting.fulfilled, (state, action) => {
                state.updateLoading = false;
                if (action.payload) {
                    const index = state.settings.findIndex(s => s._id === action.payload._id);
                    if (index !== -1) {
                        state.settings[index] = action.payload;
                    }
                }
            })
            .addCase(updateProductSetting.rejected, (state, action) => {
                state.updateLoading = false;
                state.error = action.payload;
            })
            // addSettingValue
            .addCase(addSettingValue.fulfilled, (state, action) => {
                if (action.payload) {
                    const index = state.settings.findIndex(s => s._id === action.payload._id);
                    if (index !== -1) {
                        state.settings[index] = action.payload;
                    }
                }
            })
            // removeSettingValue
            .addCase(removeSettingValue.fulfilled, (state, action) => {
                if (action.payload) {
                    const index = state.settings.findIndex(s => s._id === action.payload._id);
                    if (index !== -1) {
                        state.settings[index] = action.payload;
                    }
                }
            });
    }
});

export const selectProductSettings = (state) => state.productSettings.settings;
export const selectProductSettingsLoading = (state) => state.productSettings.loading;
export const selectProductSettingsError = (state) => state.productSettings.error;
export const selectProductSettingsCreateLoading = (state) => state.productSettings.createLoading;
export const selectProductSettingsUpdateLoading = (state) => state.productSettings.updateLoading;

export default productSettingsSlice.reducer;
